import { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { toast } from "sonner";
import { ArrowRight, Building2, ShieldCheck, Sparkles, UserPlus2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from "@/components/ui/select";
import api from "@/lib/api";

export default function Register() {
  const nav = useNavigate();
  const [busy, setBusy] = useState(false);
  const [instCode, setInstCode] = useState("");
  const [institute, setInstitute] = useState(null);
  const [asAdmin, setAsAdmin] = useState(false);

  const [form, setForm] = useState({
    name: "", email: "", password: "", phone: "",
    department: "", user_type: "staff", admin_setup_code: "",
  });
  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const lookup = async () => {
    if (!instCode.trim()) return;
    try {
      const res = await api.publicInstitute(instCode.trim());
      setInstitute(res.institute);
      toast.success(`Institute: ${res.institute.name}`);
    } catch (e) {
      setInstitute(null);
      toast.error(e.message || "Institute not found");
    }
  };

  const onSubmit = async (e) => {
    e.preventDefault();
    if (!instCode.trim()) { toast.error("Enter and verify your institute code first."); return; }
    if (form.password.length < 8) { toast.error("Password must be at least 8 characters."); return; }
    setBusy(true);
    try {
      const res = await api.register({
        institute_code: instCode.trim(),
        name: form.name.trim(),
        email: form.email.trim(),
        password: form.password,
        phone: form.phone.trim() || undefined,
        department: form.department.trim() || undefined,
        user_type: form.user_type,
        admin_setup_code: asAdmin ? form.admin_setup_code.trim() : undefined,
      });
      if (res.pending) {
        toast.success("Registered — awaiting admin approval. We'll email you once approved.");
      } else {
        toast.success(`Welcome to ${res.institute || "Solution"}! You can sign in now.`);
      }
      nav("/login", { replace: true });
    } catch (err) {
      toast.error(err.message || "Registration failed");
    } finally { setBusy(false); }
  };

  return (
    <div className="relative min-h-screen overflow-hidden">
      <div className="orb bg-cobalt-500/50" style={{ width: 520, height: 520, top: -160, left: -180 }} />
      <div className="orb bg-cyanx/40" style={{ width: 460, height: 460, top: 60, right: -120 }} />
      <div className="absolute inset-0 grid-lines pointer-events-none" />

      <header className="relative z-10 flex items-center justify-between px-4 sm:px-8 lg:px-14 py-4 sm:py-6 safe-top gap-3">
        <Link to="/" className="flex items-center gap-2.5 shrink-0" data-testid="register-brand">
          <div className="h-8 w-8 rounded-xl bg-white border border-black/5 grid place-items-center shrink-0 shadow-sm"><img src="/brand/solution-mark-t.png" alt="Solution" className="h-6 w-6 object-contain" /></div>
          <div className="font-display text-xl font-extrabold tracking-tighter">
            Solu<span className="text-cobalt-500">tion</span>
          </div>
        </Link>
        <Link to="/login" className="text-xs sm:text-sm text-muted-foreground hover:text-foreground text-right">Already have an account? <span className="text-cobalt-500 font-medium">Sign in</span></Link>
      </header>

      <main className="relative z-10 mx-auto max-w-2xl px-4 sm:px-6 lg:px-14 pt-4 pb-10 sm:pb-16">
        <div className="glass inline-flex items-center gap-1.5 rounded-full px-3 py-1 border-white/60 dark:border-white/10 mb-6">
          <Sparkles className="h-3 w-3 text-cobalt-500" />
          <span className="text-mono text-[10px] tracking-widest uppercase">Self-serve onboarding</span>
        </div>

        <motion.div
          initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.4 }}
          className="glass-strong rounded-3xl p-6 sm:p-8"
          data-testid="register-card"
        >
          <div className="flex items-center gap-2 mb-1">
            <UserPlus2 className="h-5 w-5 text-cobalt-500" />
            <h1 className="font-display text-2xl font-extrabold tracking-tighter">Create your account</h1>
          </div>
          <p className="text-sm text-muted-foreground mb-5">
            Staff & students register with the institute code from IT. Institute admins register with the secret setup code they were given.
          </p>

          <div className="rounded-2xl bg-white/60 dark:bg-white/5 border border-white/70 dark:border-white/10 p-4 mb-5">
            <div className="overline">Institute code</div>
            <div className="flex gap-2 mt-1.5">
              <div className="relative flex-1">
                <Building2 className="h-4 w-4 absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
                <Input
                  data-testid="register-institute-code"
                  placeholder="e.g. "
                  value={instCode}
                  onChange={(e) => setInstCode(e.target.value.toUpperCase())}
                  className="pl-9 rounded-xl uppercase text-mono"
                />
              </div>
              <Button type="button" variant="outline" className="rounded-xl" onClick={lookup} disabled={!instCode}>
                Verify
              </Button>
            </div>
            {institute && (
              <div className="text-xs text-emerald-600 mt-2 flex items-center gap-1.5" data-testid="register-institute-result">
                <ShieldCheck className="h-3.5 w-3.5" /> {institute.name}
                {institute.email_mode === "domain" && institute.allowed_domains && (
                  <span className="text-muted-foreground">· requires @{institute.allowed_domains.split(",")[0]}</span>
                )}
              </div>
            )}
          </div>

          <div className="flex items-center justify-between rounded-2xl bg-white/60 dark:bg-white/5 border border-white/70 dark:border-white/10 p-4 mb-5">
            <div>
              <div className="text-sm font-medium">I am the institute admin</div>
              <div className="text-xs text-muted-foreground">Uses the one-time admin setup code instead of open registration.</div>
            </div>
            <Switch checked={asAdmin} onCheckedChange={setAsAdmin} data-testid="register-as-admin" />
          </div>

          <form onSubmit={onSubmit} className="space-y-4">
            {asAdmin && (
              <div>
                <Label htmlFor="setupCode">Admin setup code</Label>
                <Input id="setupCode" data-testid="register-setup-code" value={form.admin_setup_code} onChange={set("admin_setup_code")}
                  className="mt-1.5 rounded-xl text-mono" required />
              </div>
            )}
            <div className="grid sm:grid-cols-2 gap-4">
              <div>
                <Label htmlFor="name">Full name</Label>
                <Input id="name" data-testid="register-name" value={form.name} onChange={set("name")} className="mt-1.5 rounded-xl" required />
              </div>
              <div>
                <Label htmlFor="email">Email</Label>
                <Input id="email" type="email" data-testid="register-email" value={form.email} onChange={set("email")} className="mt-1.5 rounded-xl" required />
              </div>
            </div>
            <div className="grid sm:grid-cols-2 gap-4">
              <div>
                <Label htmlFor="password">Password</Label>
                <Input id="password" type="password" data-testid="register-password" value={form.password} onChange={set("password")}
                  className="mt-1.5 rounded-xl" minLength={8} required />
              </div>
              <div>
                <Label htmlFor="phone">Phone (optional)</Label>
                <Input id="phone" data-testid="register-phone" value={form.phone} onChange={set("phone")} className="mt-1.5 rounded-xl" />
              </div>
            </div>
            {!asAdmin && (
              <div className="grid sm:grid-cols-2 gap-4">
                <div>
                  <Label>I am a</Label>
                  <Select value={form.user_type} onValueChange={(v) => setForm((f) => ({ ...f, user_type: v }))}>
                    <SelectTrigger className="mt-1.5 rounded-xl" data-testid="register-user-type"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="staff">Staff / faculty</SelectItem>
                      <SelectItem value="student">Student</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label htmlFor="department">Department (optional)</Label>
                  <Input id="department" data-testid="register-department" value={form.department} onChange={set("department")} className="mt-1.5 rounded-xl" />
                </div>
              </div>
            )}
            <Button type="submit" data-testid="register-submit" disabled={busy}
              className="w-full rounded-full h-11 bg-cobalt-500 hover:bg-cobalt-600 text-white gap-1.5">
              {busy ? "Creating account…" : (<>Create account <ArrowRight className="h-4 w-4" /></>)}
            </Button>
          </form>
        </motion.div>
      </main>
    </div>
  );
}
